import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { pendingProjectUtils } from '../utils/pendingProject';
import { eventBus } from '../events';
const ProjectCreationContext = createContext(undefined);
export const ProjectCreationProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [pendingProject, setPendingProject] = useState(() => {
        return pendingProjectUtils.retrieve();
    });
    const [hasBeenDismissed, setHasBeenDismissed] = useState(false);
    const [hasResumedPending, setHasResumedPending] = useState(false);
    // Creating a project is free — it starts a 7-day demo — so a user who
    // stashed an AOI before signing in goes straight to the create confirmation.
    const resumeCreate = useCallback((project) => {
        eventBus.emit('project:confirmCreate', { projectName: project.projectName });
    }, []);
    // A pending project survives a sign-in round trip: pick it back up as soon as
    // the user lands authenticated.
    useEffect(() => {
        if (isAuthenticated && pendingProject && !hasBeenDismissed && !hasResumedPending) {
            setHasResumedPending(true);
            // Small delay to ensure billing status has loaded
            const timer = setTimeout(() => {
                resumeCreate(pendingProject);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isAuthenticated, pendingProject, hasBeenDismissed, hasResumedPending, resumeCreate]);
    const storePendingProject = useCallback((projectData) => {
        pendingProjectUtils.store(projectData);
        setPendingProject({ ...projectData, timestamp: Date.now() });
        setHasBeenDismissed(false);
        setHasResumedPending(false);
    }, []);
    const clearPendingProject = useCallback(() => {
        pendingProjectUtils.clear();
        setPendingProject(null);
        setHasBeenDismissed(false);
        setHasResumedPending(false);
    }, []);
    const dismissPendingProject = useCallback(() => {
        setHasBeenDismissed(true);
    }, []);
    const value = useMemo(() => ({
        pendingProject,
        storePendingProject,
        clearPendingProject,
        dismissPendingProject,
    }), [
        pendingProject,
        storePendingProject,
        clearPendingProject,
        dismissPendingProject,
    ]);
    return (_jsx(ProjectCreationContext.Provider, { value: value, children: children }));
};
export const useProjectCreation = () => {
    const context = useContext(ProjectCreationContext);
    if (context === undefined) {
        throw new Error('useProjectCreation must be used within a ProjectCreationProvider');
    }
    return context;
};
